import { useEffect, useRef, useState } from 'react'
import type { Divisao } from '../api'
import {
  ancorar,
  chavesDoEstagio,
  duracaoDaFesta,
  festasACelebrar,
  gravarCelebradas,
  lerCelebradas,
  proximaDaFila,
  type FestaDeMarca,
} from '../marcosMarca'

/**
 * Dispara a comemoração de uma MARCA quando ela cruza a metade ou a meta dela.
 *
 * O que este gancho resolve, e que a lógica pura de `marcosMarca.ts` não pode:
 *
 *  - **a FILA.** Duas marcas que cruzam no mesmo ciclo não se descartam — 50 % e
 *    100 % de cada marca acontecem uma vez no evento. Elas entram em fila, da
 *    maior venda para a menor, e o telão mostra uma de cada vez.
 *  - **a comemoração GERAL passa na frente.** Enquanto `geralNoAr` for `true`, a
 *    fila espera. Duas telas cheias sobrepostas não é uma decisão de z-index: é
 *    a sala vendo confete em cima de confete e não lendo nenhum dos dois.
 *  - **a marca é registrada como celebrada ao ENTRAR NA FILA**, não ao aparecer.
 *    Se fosse ao aparecer, o snapshot seguinte — que chega a cada 5 min, com a
 *    marca ainda acima da meta — a reenfileiraria, e a festa viraria permanente.
 *    O custo é conhecido e é o lado seguro: F5 no exato intervalo entre
 *    enfileirar e exibir perde aquela festa, em vez de repeti-la para sempre.
 *  - **o temporizador é um só e é limpo no desmonte.** São quatro dias ligados.
 *
 *  - **a DEMONSTRAÇÃO não ancora e não comemora.** O painel sobe em modo
 *    demonstração até o primeiro snapshot do coletor (PUBLICAR.md), e os
 *    números fictícios põem quase toda marca acima de 50 %. Uma tela aberta
 *    nesse estado ancoraria em cima deles e as festas REAIS nunca aconteceriam —
 *    medido em 14/10, com as 8 chaves gravadas antes de o dado verdadeiro
 *    chegar. Enquanto a demonstração estiver ligada, o gancho não faz nada: a
 *    âncora acontece no primeiro snapshot de verdade.
 *
 * @param divisoes      as divisões do painel, com `vendido` e `meta` de agora
 * @param geralNoAr     `true` enquanto a comemoração do total geral está na tela
 * @param demonstracao  `true` enquanto o número da tela é fictício
 */
export function usarComemoracaoDeMarca(
  divisoes: Divisao[],
  geralNoAr: boolean,
  demonstracao: boolean,
): FestaDeMarca | null {
  const [emCartaz, setEmCartaz] = useState<FestaDeMarca | null>(null)
  /* A fila e o conjunto de celebradas vivem em ref: mudá-los não pinta nada por
     si, e guardá-los em estado faria uma repintura por snapshot que chega. O
     `versao` é o que avisa o efeito de avanço que a fila mudou. */
  const fila = useRef<FestaDeMarca[]>([])
  const celebradas = useRef<Set<string> | null>(null)
  const [versao, setVersao] = useState(0)
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    /* Número fictício não ancora nem comemora — nem a primeira leitura. É o que
       mantém a âncora para o primeiro snapshot REAL. */
    if (demonstracao) return

    const armazem = typeof localStorage === 'undefined' ? null : localStorage

    if (celebradas.current === null) {
      /* Primeira leitura da sessão: ANCORA, não comemora. O que estava guardado
         entra junto — recarregar a página não reabre festa nenhuma. */
      const guardadas = lerCelebradas(armazem, divisoes)
      const ancora = ancorar(divisoes)
      for (const chave of ancora) guardadas.add(chave)
      gravarCelebradas(armazem, ancora)
      celebradas.current = guardadas
      return
    }

    const novas = festasACelebrar(divisoes, celebradas.current)
    if (novas.length === 0) return

    for (const festa of novas) {
      /* As DUAS chaves quando o estágio é a meta: estar na meta implica ter
         passado da metade, e sem marcar as duas a festa da metade dispararia
         depois da da meta, fora de ordem. */
      const chaves = chavesDoEstagio(festa.marca, festa.tipo)
      for (const chave of chaves) celebradas.current.add(chave)
      gravarCelebradas(armazem, chaves)
      fila.current.push(festa)
    }
    setVersao(numero => numero + 1)
  }, [divisoes, demonstracao])

  /* O AVANÇO da fila. Separado do efeito acima porque ele também tem de rodar
     quando a comemoração geral sai da tela — e nesse instante as divisões não
     mudaram. */
  useEffect(() => {
    /* A decisão (uma de cada vez, geral na frente) vive em `marcosMarca.ts` para
       ter teste; aqui só se tira da fila o que ela escolheu. */
    const proxima = proximaDaFila(fila.current, emCartaz, geralNoAr)
    if (proxima === null) return
    fila.current.shift()

    setEmCartaz(proxima)
    if (temporizador.current !== null) clearTimeout(temporizador.current)
    temporizador.current = setTimeout(() => {
      temporizador.current = null
      /* Sai da tela e deixa o efeito puxar a próxima da fila, se houver. */
      setEmCartaz(null)
    }, duracaoDaFesta(proxima.tipo))
  }, [emCartaz, geralNoAr, versao])

  // Desmonte: nenhum temporizador fica pendurado.
  useEffect(
    () => () => {
      if (temporizador.current !== null) clearTimeout(temporizador.current)
    },
    [],
  )

  return emCartaz
}
